import axios from './axiosConfig'

export interface VaultRow {
 id: string
 name: string
 code: string | null
 account_type?: string
 provider_name?: string | null
 identifier_type?: string | null
 identifier?: string | null
 masked_identifier?: string | null
 currency?: string
 branch_scope?: 'all_branches' | 'selected_branches'
 is_active?: boolean
 allow_negative: boolean
 allow_sales?: boolean
 allow_refunds?: boolean
 allow_expenses?: boolean
 allow_payroll?: boolean
 allow_supplier_payments?: boolean
 allow_purchase_payments?: boolean
 allow_deposits?: boolean
 allow_withdrawals?: boolean
 allow_transfers?: boolean
 allow_owner_movements?: boolean
 allow_manual_adjustments?: boolean
 notes?: string | null
 cached_balance?: string | null
 reconciliation_difference?: string | null
 payment_methods?: Array<{
 id: string
 payment_type: string
 is_active: boolean
 is_default_fallback: boolean
 display_order: number
 }>
 branch_links?: Array<{
 id: string
 branch_id: string
 branch_name?: string | null
 payment_type: string
 is_active: boolean
 is_default: boolean
 display_order: number
 }>
 /** Decimal string from API (avoid float drift). */
 balance?: string
 branch?: { id: string; name: string; code: string | null } | null
}

type ApiList = { status: string; data: VaultRow[]; message?: string }
type ApiOne = { status: string; data: VaultRow; message?: string }

type ApiTxList = {
 status: string
 data: {
 transactions: any[]
 pagination: { total: number; per_page: number; current_page: number; last_page: number }
 }
 message?: string
}

type ApiTxOne = { status: string; data: any; message?: string }

export const vaultsAPI = {
 available: async (
   branchId: string,
   params?: { payment_type?: string; capability?: 'sales' | 'refunds' | 'expenses' | 'payroll' | 'supplier_payments' | 'purchase_payments' | 'deposits' | 'withdrawals' | 'transfers' | 'owner_movements' | 'manual_adjustments' },
 ) => {
   const response = await axios.get<{
     status: string
     data: Array<VaultRow & {
       payment_type: string
       is_default: boolean
     }>
   }>('/vaults/available', {
     params,
     headers: { 'X-Branch-Id': branchId },
   })
   return response.data
 },

  list: async (
    params?: { active_only?: boolean; search?: string; sort_by?: string; sort_dir?: string },
    options?: { xBranchId?: string | null },
  ) => {
 const config: { params?: typeof params; headers?: Record<string, string> } = { params }
 if (options?.xBranchId) {
 config.headers = { 'X-Branch-Id': options.xBranchId }
 }
 const response = await axios.get<ApiList>('/vaults', config)
 return response.data
 },

 get: async (id: string) => {
 const response = await axios.get<ApiOne>(`/vaults/${id}`)
 return response.data
 },

 create: async (payload: Record<string, unknown>) => {
 const response = await axios.post<ApiOne>('/vaults', payload)
 return response.data
 },

 update: async (id: string, payload: Record<string, unknown>) => {
 const response = await axios.put<ApiOne>(`/vaults/${id}`, payload)
 return response.data
 },

 delete: async (id: string) => {
 const response = await axios.delete<{ status: string; message?: string }>(`/vaults/${id}`)
 return response.data
 },

 transactions: async (vaultId: string, params?: { type?: string; from_date?: string; to_date?: string; per_page?: number; page?: number; sort_by?: string; sort_dir?: string }) => {
 const response = await axios.get<ApiTxList>(`/vaults/${vaultId}/transactions`, { params })
 return response.data
 },

 transactionsAll: async (params?: {
 vault_id?: string
 type?: string
 from_date?: string
 to_date?: string
 search?: string
 per_page?: number
 page?: number
 }) => {
 const response = await axios.get<ApiTxList>('/vault-transactions', { params })
 return response.data
 },

 transactionById: async (id: string) => {
 const response = await axios.get<ApiTxOne>(`/vault-transactions/${id}`)
 return response.data
 },

 deposit: async (vaultId: string, payload: { amount: number; branch_id?: string; classification: 'customer_collection' | 'other_income' | 'refund_recovery' | 'other_external_source'; date?: string; reference?: string; notes?: string; idempotency_key?: string }) => {
 const response = await axios.post<ApiTxOne>(`/vaults/${vaultId}/deposit`, payload)
 return response.data
 },

 withdraw: async (vaultId: string, payload: { amount: number; branch_id?: string; classification: 'operating_expense' | 'other_external_use'; date?: string; reference?: string; notes?: string; idempotency_key?: string }) => {
 const response = await axios.post<ApiTxOne>(`/vaults/${vaultId}/withdraw`, payload)
 return response.data
 },

 transfer: async (payload: { from_vault_id: string; to_vault_id: string; branch_id?: string; amount: number; date?: string; reference?: string; notes?: string; idempotency_key?: string }) => {
 const response = await axios.post<ApiTxOne>('/vaults/transfer', payload)
 return response.data
 },

 financialMovement: async (payload: {
  vault_id: string
  branch_id: string
  classification: 'capital_contribution' | 'owner_drawing' | 'capital_adjustment_in' | 'capital_adjustment_out' | 'loan_received' | 'loan_principal_repayment' | 'loan_interest'
  amount: number
  occurred_at: string
  reference?: string
  notes: string
  idempotency_key: string
 }) => {
  const response = await axios.post<ApiTxOne>('/financial-movements', payload)
  return response.data
 },

 reconciliations: async (vaultId: string, params?: { from_date?: string; to_date?: string; per_page?: number }) => {
 const response = await axios.get<{ status: string; data: any }>(`/vaults/${vaultId}/reconciliations`, { params })
 return response.data
 },

 reconcile: async (vaultId: string, payload: { actual_balance: number; date?: string; notes?: string }) => {
 const response = await axios.post<ApiTxOne>(`/vaults/${vaultId}/reconcile`, payload)
 return response.data
 },
}
